/* eslint-disable @typescript-eslint/no-explicit-any */
import mitt from 'mitt';
import { 
// @ts-ignore
Inviter, 
// @ts-ignore
Referral, 
// @ts-ignore
Registerer, 
// @ts-ignore
RegistererState, 
// @ts-ignore
RequestPendingError, 
// @ts-ignore
SessionState, 
// @ts-ignore
UserAgent, } from 'sip.js';
import { CallStatusTypes, Events, PhoneStatusTypes, SpyTypes, } from './types.js';
import logdown from 'logdown';
function setupRemoteMedia(session) {
    const remoteStream = new MediaStream();
    const mediaElement = document.getElementById('audio-remote');
    if (mediaElement) {
        session.sessionDescriptionHandler.peerConnection
            .getReceivers()
            .forEach((receiver) => {
            if (receiver.track) {
                remoteStream.addTrack(receiver.track);
            }
        });
        mediaElement.srcObject = remoteStream;
        mediaElement.play();
    }
    else {
        throw new Error('audio element with #audio-remote required');
    }
}
function cleanupMedia() {
    const mediaElement = document.getElementById('audio-remote');
    if (mediaElement) {
        mediaElement.srcObject = null;
        mediaElement.pause();
    }
    else {
        throw new Error('audio element with #audio-remote required');
    }
}
export default class Phone {
    userAgent;
    phoneServer;
    // @ts-ignore
    emitter = mitt();
    phoneStatus;
    registerer;
    options;
    attemptingReconnection = false;
    logger = logdown('@letsdial/phone', {
        prefixColor: '#134E4A',
    });
    session;
    callStatus;
    sessionInConference = false;
    listeners = {};
    onlineListener;
    constructor(phoneServer, options = {}) {
        if (options.debug) {
            this.logger.state.isEnabled = options.debug ?? false;
        }
        options = {
            reconnectionAttempts: 3,
            reconnectionDelay: 4,
            shouldBeConnected: true,
            debug: false,
            ...options,
        };
        this.logger.info('Options Configured', options);
        this.phoneServer = phoneServer;
        this.options = options;
    }
    on(event, callback) {
        if (this.options.disableEvents) {
            if (!this.listeners[event]) {
                this.listeners[event] = [];
            }
            this.listeners[event].push(callback);
        }
        else {
            //@ts-expect-error It is a callback
            this.emitter.on(event, callback);
        }
    }
    off(event, callback) {
        if (this.options.disableEvents) {
            if (this.listeners[event]) {
                this.listeners[event] = this.listeners[event].filter((cb) => cb !== callback);
            }
        }
        else {
            //@ts-expect-error It is a callback
            this.emitter.off(event, callback);
        }
    }
    emit(event, data) {
        this.logger.info(event, data);
        if (this.options.disableEvents) {
            if (this.listeners[event]) {
                for (const callBack of this.listeners[event]) {
                    callBack(data);
                }
            }
            if (this.listeners['*']) {
                for (const callBack of this.listeners['*']) {
                    callBack(event, data);
                }
            }
        }
        else {
            this.emitter.emit(event, data);
        }
    }
    clearListeners() {
        if (!this.options.disableEvents) {
            //@ts-expect-error All does exists
            this.emitter.all.clear();
        }
        this.listeners = {};
    }
    /**
     * Starts the connection and creates the userAgent
     * @param username
     * @param password
     */
    connect(username, password) {
        if (this.userAgent &&
            (this.phoneStatus === PhoneStatusTypes.CONNECTING ||
                this.phoneStatus === PhoneStatusTypes.CONNECTED ||
                this.phoneStatus === PhoneStatusTypes.REGISTERING ||
                this.phoneStatus === PhoneStatusTypes.REGISTERED)) {
            this.logger.info('Phone already connected or connecting', this.phoneStatus);
            return;
        }
        const uri = UserAgent.makeURI(`sip:${username}@${this.phoneServer}:4443`);
        if (!uri) {
            throw new Error('Failed to create URI');
        }
        const userAgentOptions = {
            uri,
            authorizationUsername: username,
            authorizationPassword: password,
            transportOptions: {
                server: `wss://${this.phoneServer}:4443`,
            },
            logBuiltinEnabled: this.logger.state.isEnabled,
            sessionDescriptionHandlerFactoryOptions: {
                constraints: {
                    video: false,
                    audio: true,
                },
                peerConnectionConfiguration: {
                    iceServers: [
                        {
                            urls: `turn:${this.phoneServer}:5349?transport=tcp`,
                            username: 'water',
                            credential: '#D039LOIj$bxN*py2#f%gLY^VzbS#Se72PKuwAk',
                        },
                        {
                            urls: `turns:${this.phoneServer}:3478?transport=tcp`,
                            username: 'water',
                            credential: '#D039LOIj$bxN*py2#f%gLY^VzbS#Se72PKuwAk',
                        },
                    ],
                },
            },
        };
        this.userAgent = new UserAgent(userAgentOptions);
        this.connectionListeners();
        this.setPhoneStatus(PhoneStatusTypes.CONNECTING);
        this.userAgent.start().catch(() => {
            this.setPhoneStatus(PhoneStatusTypes.DISCONNECTED, 'Connection to Lets Dial VOIP Failed');
            this.attemptReconnect();
        });
    }
    async close() {
        this.logger.info('Stopping Phone');
        this.options.shouldBeConnected = false;
        if (this.onlineListener) {
            window.removeEventListener('online', this.onlineListener);
        }
        try {
            await this.registerer?.unregister();
        }
        catch (e) {
            this.logger.error('Failed to unregister', e);
        }
        await this.userAgent?.stop();
        this.clearListeners();
    }
    /**
     * Sets the phone status event and emits the PhoneStatusEvent
     * @param status
     * @param error
     */
    setPhoneStatus(status, error) {
        if (error) {
            this.logger.error(`Phone Error ${error}`);
        }
        this.emit(Events.PHONE_STATUS, { status, error });
        this.phoneStatus = status;
    }
    /**
     * Sets call status and emits event
     * @param status
     * @param error
     */
    setCallStatus(status, error) {
        this.logger.debug(`CALL Status Change ${status}`);
        this.emit(Events.CALL_STATUS, { status, error });
        this.callStatus = status;
        if (status === CallStatusTypes.TERMINATED) {
            if (this.sessionInConference) {
                this.emit(Events.CONFERENCE_LEAVING, null);
            }
            this.session = undefined;
            this.sessionInConference = false;
        }
    }
    callStatusListener(newState) {
        switch (newState) {
            case SessionState.Establishing:
                this.setCallStatus(CallStatusTypes.RINGING);
                // Session is establishing.
                break;
            case SessionState.Established:
                this.setCallStatus(CallStatusTypes.ANSWERED);
                setupRemoteMedia(this.session);
                // Session has been established.
                break;
            case SessionState.Terminated:
                if (!this.sessionInConference) {
                    cleanupMedia();
                    this.setCallStatus(CallStatusTypes.TERMINATED);
                }
                // Session has terminated.
                break;
            default:
                break;
        }
    }
    async spy(asteriskServerIp, channelName, type) {
        let prefix = '';
        const extraHeaders = [
            `Server: ${asteriskServerIp}`,
            `X-Server: ${asteriskServerIp}`,
        ];
        switch (type) {
            case SpyTypes.LISTEN:
                prefix = '5555691';
                extraHeaders.push(`CHANNEL: ${channelName}`);
                extraHeaders.push(`X-Channel: ${channelName}`);
                break;
            case SpyTypes.WHISPER:
                prefix = '5555692';
                extraHeaders.push(`CHANNEL: ${channelName}`);
                extraHeaders.push(`X-Channel: ${channelName}`);
                break;
            case SpyTypes.BARGE:
                prefix = '5555693';
                extraHeaders.push(`CHANNEL: ${channelName}`);
                extraHeaders.push(`X-Channel: ${channelName}`);
                break;
            default:
                prefix = '5555691';
                extraHeaders.push(`CHANNEL: ${channelName}`);
                extraHeaders.push(`X-Channel: ${channelName}`);
                break;
        }
        const uri = UserAgent.makeURI(`sip:${prefix}@${this.phoneServer}`);
        if (!uri) {
            throw new Error('Failed to create target URI.');
        }
        if (this.userAgent) {
            this.setCallStatus(CallStatusTypes.INITIATING);
            // store.dispatch('call/setNumber', number)
            // Create new Session instance in "initial" state
            this.session = new Inviter(this.userAgent, uri, {
                extraHeaders,
            });
            // Setup session state change handler
            const sessionId = this.session.id;
            this.session.stateChange.addListener((newState) => {
                if (sessionId === this.session?.id) {
                    switch (newState) {
                        case SessionState.Establishing:
                            this.setCallStatus(CallStatusTypes.RINGING);
                            // this.ringtone.play()
                            break;
                        case SessionState.Established:
                            setupRemoteMedia(this.session);
                            this.setCallStatus(CallStatusTypes.ANSWERED);
                            // this.ringtone.pause()
                            break;
                        case SessionState.Terminated:
                            cleanupMedia();
                            this.setCallStatus(CallStatusTypes.TERMINATED);
                            break;
                    }
                }
            });
            // Options including delegate to capture response messages
            const inviteOptions = {
                requestDelegate: {
                // onAccept: response => {
                //   console.log('Positive response = ' + JSON.stringify(response))
                // },
                // onReject: response => {
                //   console.log('Negative response = ' + JSON.stringify(response))
                // }
                },
                sessionDescriptionHandlerOptions: {
                    constraints: {
                        audio: true,
                        video: false,
                    },
                    peerConnectionConfiguration: {
                        iceServers: [
                            {
                                urls: `turn:${this.phoneServer}:5349?transport=tcp`,
                                username: 'water',
                                credential: '#D039LOIj$bxN*py2#f%gLY^VzbS#Se72PKuwAk',
                            },
                            {
                                urls: `turns:${this.phoneServer}:3478?transport=tcp`,
                                username: 'water',
                                credential: '#D039LOIj$bxN*py2#f%gLY^VzbS#Se72PKuwAk',
                            },
                        ],
                    },
                },
            };
            // Send initial INVITE
            this.session.invite(inviteOptions);
        }
    }
    /**
     * Auto Reconnection of phone
     * @param reconnectionAttempt
     */
    attemptReconnect(reconnectionAttempt = 1) {
        // If not intentionally connected, don't reconnect.
        if (!this.options.shouldBeConnected) {
            return;
        }
        // Reconnection attempt already in progress
        if (this.attemptingReconnection) {
            return;
        }
        // Reconnection maximum attempts reached
        if (reconnectionAttempt > (this.options.reconnectionAttempts ?? 3)) {
            return;
        }
        this.attemptingReconnection = true;
        setTimeout(() => {
            // If not intentionally connected, don't reconnect.
            if (!this.options.shouldBeConnected) {
                this.attemptingReconnection = false;
                return;
            }
            this.logger.debug(`Attempting reconnection attempt: ${reconnectionAttempt}`);
            // Attempt reconnect
            this.userAgent
                ?.reconnect()
                .then(() => {
                // Reconnect attempt succeeded
                this.attemptingReconnection = false;
            })
                .catch(() => {
                // Reconnect attempt failed
                this.attemptingReconnection = false;
                this.attemptReconnect(++reconnectionAttempt);
            });
        }, reconnectionAttempt === 1
            ? 0
            : (this.options.reconnectionDelay ?? 4) * 1000);
    }
    async hangup() {
        if (this.session) {
            if (this.sessionInConference) {
                this.sessionInConference = false;
            }
            if (this.session instanceof Referral) {
                this.session.reject();
            }
            else {
                if (this.session.state === 'Established') {
                    this.session.bye();
                }
                else {
                    if (this.session instanceof Inviter) {
                        this.session.cancel();
                    }
                }
            }
        }
    }
    /**
     * Register UA Listeners
     */
    connectionListeners() {
        if (this.userAgent) {
            this.userAgent.delegate = {
                onConnect: () => {
                    this.register();
                    this.setPhoneStatus(PhoneStatusTypes.CONNECTED);
                },
                onDisconnect: (error) => {
                    this.registerer?.unregister();
                    if (error) {
                        this.setPhoneStatus(PhoneStatusTypes.DISCONNECTED, 'Disconnected from Lets Dial VOIP');
                        this.attemptReconnect();
                    }
                },
                onRefer: async (referral) => {
                    // Determine if you should accept or reject the referral
                    await referral.accept();
                    const inviter = referral.makeInviter();
                    this.session = inviter;
                    await inviter.invite();
                },
                onInvite: async (invitation) => {
                    this.session = invitation;
                    const sessionId = this.session.id;
                    this.session.stateChange.addListener((newState) => {
                        if (sessionId === this.session?.id) {
                            switch (newState) {
                                case SessionState.Establishing:
                                    this.setCallStatus(CallStatusTypes.RINGING);
                                    // Session is establishing.
                                    break;
                                case SessionState.Established:
                                    this.setCallStatus(CallStatusTypes.ANSWERED);
                                    setupRemoteMedia(this.session);
                                    // Session has been established.
                                    break;
                                case SessionState.Terminated:
                                    if (!this.sessionInConference) {
                                        this.setCallStatus(CallStatusTypes.TERMINATED);
                                        cleanupMedia();
                                    }
                                    // Session has terminated.
                                    break;
                                default:
                                    break;
                            }
                        }
                    });
                    const acceptOptions = {};
                    invitation.accept(acceptOptions);
                },
            };
        }
        this.onlineListener = () => {
            this.attemptReconnect();
        };
        window.addEventListener('online', this.onlineListener);
    }
    /**
     * Fires Registration
     */
    register() {
        if (!this.userAgent) {
            return;
        }
        /*
         * Create a Registerer to register user agent
         */
        this.registerer = new Registerer(this.userAgent);
        this.registerer.stateChange.addListener((newState) => {
            this.logger.info('Registration State Changes', newState);
            switch (newState) {
                case RegistererState.Registered:
                    this.setPhoneStatus(PhoneStatusTypes.REGISTERED);
                    break;
                case RegistererState.Unregistered:
                    this.setPhoneStatus(PhoneStatusTypes.UNREGISTERED);
                    break;
                case RegistererState.Terminated:
                    this.setPhoneStatus(PhoneStatusTypes.TERMINATED);
                    break;
            }
        });
        this.setPhoneStatus(PhoneStatusTypes.REGISTERING);
        this.registerer
            .register({
            requestDelegate: {
                onReject: (err) => {
                    this.setPhoneStatus(PhoneStatusTypes.TERMINATED, err.message.reasonPhrase);
                },
            },
        })
            .catch((err) => {
            this.setPhoneStatus(PhoneStatusTypes.TERMINATED, err.message);
        });
    }
    call(number, call, additionalOptions = {}) {
        const extraHeaders = [
            `Call-Type: ${call.type}`,
            `X-Call-ID: ${call.id}`,
            `Recording-Filename: ${call.attributes.recordingFileName}`,
        ];
        const dialCode = '27';
        const prefix = additionalOptions.prefix ?? '0';
        if (additionalOptions.clid) {
            extraHeaders.push(`CLID: ${additionalOptions.clid}`);
        }
        if (additionalOptions.trunkName) {
            extraHeaders.push(`Trunk: ${additionalOptions.trunkName}`);
        }
        if (additionalOptions.duelRing) {
            extraHeaders.push(`DuelRing: 1`);
        }
        else {
            extraHeaders.push(`DuelRing: 0`);
        }
        if (call?.relationships?.organization?.data?.id) {
            extraHeaders.push(`Organization: ${call.relationships.organization.data.id}`);
        }
        if (!this.userAgent) {
            throw new Error('You need to be connected first before you can make calls');
        }
        const uri = UserAgent.makeURI(`sip:${number.indexOf(dialCode) === 0
            ? `${prefix}${number.substr(dialCode.length, number.length)}`
            : number}@${this.phoneServer}`);
        if (!uri) {
            throw new Error('Failed to create target URI.');
        }
        const inviterOptions = { extraHeaders };
        const inviter = new Inviter(this.userAgent, uri, inviterOptions);
        // An Inviter is a Session
        this.session = inviter;
        const sessionId = this.session.id;
        this.session.stateChange.addListener((newState) => {
            if (sessionId === this.session?.id) {
                switch (newState) {
                    case SessionState.Establishing:
                        this.setCallStatus(CallStatusTypes.RINGING);
                        // Session is establishing.
                        break;
                    case SessionState.Established:
                        this.setCallStatus(CallStatusTypes.ANSWERED);
                        setupRemoteMedia(this.session);
                        // Session has been established.
                        break;
                    case SessionState.Terminated:
                        if (!this.sessionInConference) {
                            cleanupMedia();
                            this.setCallStatus(CallStatusTypes.TERMINATED);
                        }
                        // Session has terminated.
                        break;
                    default:
                        break;
                }
            }
        });
        inviter
            .invite()
            .then(() => {
            this.setCallStatus(CallStatusTypes.INITIATING);
        })
            .catch((error) => {
            this.logger.error(Error);
            this.setCallStatus(CallStatusTypes.TERMINATED, error.message);
        });
    }
    async hold() {
        if (this.session) {
            const sessionDescriptionHandlerOptions = {
                hold: true, // set to false to "unhold" session
            };
            this.session.sessionDescriptionHandlerOptionsReInvite =
                sessionDescriptionHandlerOptions;
            const options = {
                requestDelegate: {
                    onAccept: () => {
                        this.setCallStatus(CallStatusTypes.ON_HOLD);
                        // session is on hold
                    },
                    onReject: () => {
                        // re-invite request was rejected, call not on hold
                    },
                },
            };
            this.session.invite(options).catch((error) => {
                if (error instanceof RequestPendingError) {
                    // a hold request is already in progress
                }
            });
        }
    }
    async unHold() {
        if (this.session) {
            const sessionDescriptionHandlerOptions = {
                hold: false, // set to false to "unhold" session
            };
            this.session.sessionDescriptionHandlerOptionsReInvite =
                sessionDescriptionHandlerOptions;
            const options = {
                requestDelegate: {
                    onAccept: () => {
                        this.setCallStatus(CallStatusTypes.ANSWERED);
                        // session is on hold
                    },
                    onReject: () => {
                        // console.log('session:on:hold:rejected');
                        // re-invite request was rejected, call not on hold
                    },
                },
            };
            this.session.invite(options).catch((error) => {
                if (error instanceof RequestPendingError) {
                    // a hold request is already in progress
                }
            });
        }
    }
    async referExternal(number) {
        if (!this.userAgent) {
            throw new Error('Needs to be connected');
        }
        if (!this.session) {
            throw new Error('There needs to be a call to refer');
        }
        this.logger.info(`Referring to sip:555555${number}@${this.phoneServer}:4443`);
        const uri = UserAgent.makeURI(`sip:555555${number}@${this.phoneServer}:4443`);
        if (uri) {
            await this.session.refer(uri);
            await this.session.bye();
        }
    }
    async referToExtension(extension, callID, type, number, campaignId, recordId) {
        if (!this.userAgent) {
            throw new Error('Needs to be connected');
        }
        if (!this.session) {
            throw new Error('There needs to be a call to refer');
        }
        const uri = UserAgent.makeURI(`sip:5555678~${extension}~${callID}~${type}~${number}~${campaignId}~${recordId}@${this.phoneServer}`);
        if (!uri) {
            throw new Error('Failed to create target URI.');
        }
        await this.session.refer(uri, {
            requestOptions: {
                extraHeaders: [
                    `X-Call-ID: ${callID}`,
                    `Call-Type: ${type}`,
                    `Extension: ${extension}`,
                    `Number: ${number}`,
                    `Record-ID: ${recordId}`,
                    `Campaign-ID: ${campaignId}`,
                ],
            },
        });
        await this.session.bye();
    }
    sendDtmf(tone) {
        if (this.session && this.session.sessionDescriptionHandler) {
            this.session.sessionDescriptionHandler.sendDtmf(tone);
        }
    }
    joinConference(recordId, asteriskServerIp) {
        if (this.userAgent && this.phoneStatus === PhoneStatusTypes.REGISTERED) {
            const uri = UserAgent.makeURI(`sip:5555680_1~${recordId}@${this.phoneServer}`);
            if (!uri) {
                throw new Error('Failed to create target URI.');
            }
            this.emit(Events.CONFERENCE_JOINING, { roomId: recordId });
            this.setCallStatus(CallStatusTypes.INITIATING);
            const extraHeaders = [
                `X-RoomCode: 1~${recordId}`,
                `Server: ${asteriskServerIp}`,
                `X-Server: ${asteriskServerIp}`,
            ];
            const inviter = new Inviter(this.userAgent, uri, {
                extraHeaders,
            });
            this.session = inviter;
            const sessionId = this.session.id;
            this.session.stateChange.addListener((newState) => {
                if (sessionId === this.session?.id) {
                    switch (newState) {
                        case SessionState.Establishing:
                            this.setCallStatus(CallStatusTypes.RINGING);
                            // Session is establishing.
                            break;
                        case SessionState.Established:
                            this.setCallStatus(CallStatusTypes.ANSWERED);
                            setupRemoteMedia(this.session);
                            // Session has been established.
                            break;
                        case SessionState.Terminated:
                            cleanupMedia();
                            this.setCallStatus(CallStatusTypes.TERMINATED);
                            // Session has terminated.
                            break;
                        default:
                            break;
                    }
                }
            });
            inviter
                .invite()
                .then(() => {
                this.sessionInConference = true;
                this.setCallStatus(CallStatusTypes.INITIATING);
            })
                .catch((error) => {
                this.logger.error(Error);
                this.setCallStatus(CallStatusTypes.TERMINATED, error.message);
            });
        }
        else {
            throw new Error('Needs to be connected');
        }
    }
    async referToQueue(queueId, callID) {
        if (!this.userAgent) {
            throw new Error('Needs to be connected');
        }
        if (!this.session) {
            throw new Error('There needs to be a call to refer');
        }
        const uri = UserAgent.makeURI(`sip:5555681_${callID}_${queueId}@${this.phoneServer}`);
        if (!uri) {
            throw new Error('Failed to create target URI.');
        }
        await this.session.refer(uri, {
            requestOptions: {
                extraHeaders: [
                    `Call-ID: ${callID}`,
                    `Queue-ID: ${queueId}`,
                ],
            },
        });
        await this.session.bye();
    }
    async referToConference(recordId) {
        if (!this.userAgent) {
            throw new Error('Needs to be connected');
        }
        if (!this.session) {
            throw new Error('There needs to be a call to refer');
        }
        const uri = UserAgent.makeURI(`sip:5555680_1~${recordId}@${this.phoneServer}`);
        if (!uri) {
            throw new Error('Failed to create target URI.');
        }
        await this.session.refer(uri, {
            requestOptions: {
                extraHeaders: [`X-RoomCode: 1~${recordId}`],
            },
        });
    }
}
